/**
 * One person, and every name Trellis might recognise them by: their account on each linked system
 * the contract has, and their alternative addresses. Reached by clicking a name on the Roster
 * (docs/2026-09-27-person-identity-page.md).
 *
 * The EUA username is not a field. On Data Center the account name *is* the EUA username, so it is
 * shown beside the system it came from and set by the one username field, never typed twice.
 */
import { useState } from "preact/hooks";
import { fmtDayFull } from "@trellis/core";
import { Link } from "../components/link.js";
import { Picker } from "../components/picker.js";

export interface PersonSystem {
  integrationId: string;
  name: string;
  kind: string;
  deployment: string;
  account?: { externalId: string; displayName: string | null; mappedBy: string };
  eua?: string;
  candidates: { externalId: string; displayName: string | null; email: string | null; note: string }[];
}

export interface PersonAddress {
  value: string;
  source: string;
  statedBy: string | null;
  statedAt: string;
  removable: boolean;
}

export interface PersonIdentityView {
  programId: string;
  programName: string;
  person: { upn: string; displayName: string };
  systems: PersonSystem[];
  addresses: PersonAddress[];
}

export interface PersonIdentityScreenProps {
  view: PersonIdentityView;
  /** The account a lead arrived with from the unmapped list, as `<integrationId>:<externalId>`. */
  preset?: string | undefined;
  onSetAccount: (integrationId: string, externalId: string) => Promise<void>;
  onClearAccount: (integrationId: string, externalId: string) => Promise<void>;
  onAddAddress: (value: string) => Promise<void>;
  onRemoveAddress: (value: string) => Promise<void>;
  notice?: { text: string; error?: boolean } | undefined;
}

const systemKind = (s: PersonSystem) => `${s.kind === "jira" ? "Jira" : "Confluence"} · ${s.deployment === "datacenter" ? "Data Center" : "Cloud"}`;

/** What the instance calls an account, in the words the instance uses for it. */
const usernameLabel = (s: PersonSystem) => (s.deployment === "datacenter" ? "Username on" : "Account id on");

export function PersonIdentityScreen({ view, preset, onSetAccount, onClearAccount, onAddAddress, onRemoveAddress, notice }: PersonIdentityScreenProps) {
  const roster = `/app/programs/${encodeURIComponent(view.programId)}/roster`;
  const [address, setAddress] = useState("");
  const [busy, setBusy] = useState<string | undefined>(undefined);
  const run = async (key: string, fn: () => Promise<void>) => { setBusy(key); try { await fn(); } finally { setBusy(undefined); } };
  const addAddress = () => run("address", async () => { await onAddAddress(address.trim()); setAddress(""); });
  // One EUA username, however many Data Center systems carry it. Two different ones would mean the
  // instances disagree, so both are named rather than one being picked.
  const euaNames = [...new Set(view.systems.map((s) => s.eua).filter((v): v is string => !!v))];
  return (
    <main class="main settings">
      <div class="page-head">
        <span class="label"><Link class="link" href={roster}>{view.programName} · Roster</Link></span>
        <h1 class="h-lg">{view.person.displayName}</h1>
        <p class="lede">
          Every name Trellis might recognise {view.person.displayName} by. Anything read under one of
          these goes onto this one record. It does not change who counts as customer personnel: an ask
          is still an ask because the customer register says who the customer is.
        </p>
        <p class="note dim code">{view.person.upn}</p>
        {notice && <div class={`notice${notice.error ? " error" : ""}`}>{notice.text}</div>}
      </div>

      <section class="panel">
        <div class="panel-head"><span class="label">Accounts on linked systems</span></div>
        {view.systems.length === 0 && (
          <div class="line">
            <span class="dot off" />
            <span class="body muted">
              This contract links no Jira project or Confluence space yet, so there is no system to
              name an account on. Link one on the <Link class="link" href={`/app/programs/${encodeURIComponent(view.programId)}`}>contract page</Link>.
            </span>
          </div>
        )}
        {view.systems.map((s) => (
          <SystemRow
            key={s.integrationId}
            system={s}
            person={view.person.displayName}
            preset={preset && preset.startsWith(`${s.integrationId}:`) ? preset.slice(s.integrationId.length + 1) : undefined}
            busy={busy === s.integrationId}
            onSet={(externalId) => run(s.integrationId, () => onSetAccount(s.integrationId, externalId))}
            onClear={(externalId) => run(s.integrationId, () => onClearAccount(s.integrationId, externalId))}
          />
        ))}
        {euaNames.length > 0 && (
          <div class="panel-foot">
            CMS knows {view.person.displayName} as {euaNames.map((v, i) => <>{i ? " and " : ""}<span class="code">{v}</span></>)}.
            That is the Data Center account name above, not a second thing to set, and it is how Trellis
            places {view.person.displayName} wherever CMS names people that way.
          </div>
        )}
      </section>

      <section class="panel">
        <div class="panel-head"><span class="label">Other addresses</span></div>
        <div class="s">
          A customer address this person answers to. A Samtek alias does not go here: the directory
          states those through the tenant and this contract cannot override them.
        </div>
        {view.addresses.length === 0 && (
          <div class="line">
            <span class="dot off" />
            <span class="body muted">
              No other address recorded. Add the address a customer's Jira or Confluence knows them by.
            </span>
          </div>
        )}
        {view.addresses.map((a) => (
          <div class="line" key={a.value}>
            <span class={`dot ${a.source === "directory" ? "navy" : "good"}`} />
            <span class="body"><span class="code">{a.value}</span>
              <div class="s">{a.source === "directory"
                ? "Samtek alias, from the directory"
                : `Customer address, stated by ${a.statedBy ?? "a lead"} on ${fmtDayFull(a.statedAt)}`}</div>
            </span>
            {a.removable && (
              <span class="row-actions">
                <button class="btn quiet" disabled={busy === a.value}
                  onClick={() => void run(a.value, () => onRemoveAddress(a.value))}>
                  Remove<span class="sr-only"> {a.value} from {view.person.displayName}</span>
                </button>
              </span>
            )}
          </div>
        ))}
        <div class="inline-form">
          <div class="s"><label for="add-address">Another address for {view.person.displayName}</label></div>
          <input id="add-address" type="email" value={address} spellcheck={false}
            placeholder="firstname.lastname@cms.hhs.gov" aria-describedby="add-address-help"
            onInput={(e) => setAddress((e.target as HTMLInputElement).value)} />
          <div class="s" id="add-address-help">Anything Trellis reads from this address lands on this record.</div>
          <div class="buttons">
            <button class="btn" disabled={!address.trim() || busy === "address"} onClick={() => void addAddress()}>Add this address</button>
          </div>
        </div>
      </section>
    </main>
  );
}

/**
 * One system, and one field. The options are the accounts this instance named that nobody placed,
 * because "I recognise that name" is the common case and retyping it is how a typo gets into a key;
 * typing is still allowed, because an account Trellis has not seen yet is a real account.
 */
function SystemRow({ system, person, preset, busy, onSet, onClear }: {
  system: PersonSystem;
  person: string;
  preset?: string | undefined;
  busy: boolean;
  onSet: (externalId: string) => Promise<void>;
  onClear: (externalId: string) => Promise<void>;
}) {
  const [choice, setChoice] = useState(preset ?? "");
  const id = `account-${system.integrationId}`;
  const account = system.account;
  return (
    <div class="line">
      <span class={`dot ${account ? "good" : "off"}`} />
      <span class="body">{system.name}
        <div class="s">{systemKind(system)}{account ? <> · <span class="code">{account.externalId}</span>{account.displayName ? ` · ${account.displayName}` : ""}</> : " · no account named"}</div>
        {!account && (
          <div class="inline-form">
            <Picker id={id} label={`${usernameLabel(system)} ${system.name}`} showLabel
              placeholder={system.deployment === "datacenter" ? "YDVN, or search the names seen here" : "Account id, or search the names seen here"}
              options={system.candidates.map((c) => ({ value: c.externalId, label: c.displayName ?? c.externalId, hint: [c.externalId, c.email, c.note].filter(Boolean).join(" · ") }))}
              value={choice} onChange={setChoice} clearable allowCustom="Use this name" />
            <div class="s">
              {system.candidates.length > 0
                ? `The names ${system.name} has used that nobody has placed, or type one Trellis has not seen.`
                : `Trellis has seen no unplaced account here yet, so type the name ${system.name} calls them.`}
            </div>
            <div class="buttons">
              <button class="btn" disabled={!choice.trim() || busy} onClick={() => void onSet(choice.trim())}>
                Save<span class="sr-only"> {person}&rsquo;s username on {system.name}</span>
              </button>
            </div>
          </div>
        )}
      </span>
      {account && (
        <span class="row-actions">
          <button class="btn quiet" disabled={busy} onClick={() => void onClear(account.externalId)}>
            Clear<span class="sr-only"> {account.externalId} from {person} on {system.name}</span>
          </button>
        </span>
      )}
    </div>
  );
}
